import type {
	AttentionQueue,
	ChangeBidStatusCommand,
	ChangeDealerStatusCommand,
	ChangeVehicleStatusCommand,
	ConfirmPaymentCommand,
	CostRates,
	CreateDestinationCommand,
	CreditBalanceCommand,
	CreditTopUpCommand,
	DealerStatusChangeRef,
	DealerTimelineEvent,
	DeclineTopUpCommand,
	DemoteOperatorCommand,
	DepositTopUp,
	DestinationUsage,
	InlandPriceList,
	Invoice,
	InvoicePreview,
	InvoicePreviewQuery,
	LedgerEntryRef,
	OperatorAccount,
	OperatorBid,
	OperatorBidsSummary,
	OperatorDealer,
	OperatorDealerDetail,
	OperatorDealerProfile,
	OperatorDealersSummary,
	OperatorInvoice,
	OperatorInvoicesSummary,
	OperatorListQuery,
	OperatorTopUp,
	OperatorTopUpsSummary,
	OperatorVehicle,
	OperatorVehiclesSummary,
	Paginated,
	PaymentDetails,
	PromoteOperatorCommand,
	RemoveDestinationResult,
	ResolveBidChangeCommand,
	UpdateCostRatesCommand,
	UpdatePaymentDetailsCommand,
	VehicleDocumentKind,
} from '@sirius/contracts';

import { request } from '../client';

const json = (body: unknown, method: 'POST' | 'PATCH'): RequestInit => ({
	method,
	headers: { 'Content-Type': 'application/json' },
	body: JSON.stringify(body),
});

/** Черга цілком або лише одного дилера — для блоку «Waiting on you» у його профілі. */
export const listAttention = (dealer?: string): Promise<AttentionQueue> =>
	request<AttentionQueue>(`/operator/attention${dealer ? `?dealer=${encodeURIComponent(dealer)}` : ''}`);

/** Параметри списку в адресу запиту. Порожні не шлемо: URL має лишатись читним. */
const qs = (query: OperatorListQuery): string => {
	const params = new URLSearchParams();
	if (query.id) params.set('id', query.id);
	if (query.dealer) params.set('dealer', query.dealer);
	if (query.search) params.set('search', query.search);
	if (query.status) params.set('status', query.status);
	if (query.destination) params.set('destination', query.destination);
	if (query.sort) params.set('sort', query.sort);
	if (query.dir) params.set('dir', query.dir);
	if (query.page && query.page > 1) params.set('page', String(query.page));
	if (query.perPage) params.set('perPage', String(query.perPage));
	const text = params.toString();
	return text ? `?${text}` : '';
};

export const getBidsSummary = (): Promise<OperatorBidsSummary> =>
	request<OperatorBidsSummary>('/operator/bids/summary');

export const listBids = (query: OperatorListQuery): Promise<Paginated<OperatorBid>> =>
	request<Paginated<OperatorBid>>(`/operator/bids${qs(query)}`);

export const changeBidStatus = (id: string, command: ChangeBidStatusCommand): Promise<OperatorBid> =>
	request<OperatorBid>(`/operator/bids/${encodeURIComponent(id)}/status`, json(command, 'PATCH'));

/** Інвойс, який створить Won, з тими самими полями, що у формі. */
export const getInvoicePreview = (id: string, query: InvoicePreviewQuery): Promise<InvoicePreview> => {
	const params = new URLSearchParams();
	if (query.finalPriceUsd) params.set('finalPriceUsd', query.finalPriceUsd);
	if (query.inlandUsd) params.set('inlandUsd', query.inlandUsd);
	const text = params.toString();
	return request<InvoicePreview>(`/operator/bids/${encodeURIComponent(id)}/invoice-preview${text ? `?${text}` : ''}`);
};

/** Рішення за запитом дилера на зміну ставки. */
export const resolveBidChange = (id: string, command: ResolveBidChangeCommand): Promise<OperatorBid> =>
	request<OperatorBid>(`/operator/bid-changes/${encodeURIComponent(id)}/resolve`, json(command, 'POST'));

export const getDealer = (id: string): Promise<OperatorDealerDetail> =>
	request<OperatorDealerDetail>(`/operator/dealers/${encodeURIComponent(id)}`);

export const getDealerProfile = (id: string): Promise<OperatorDealerProfile> =>
	request<OperatorDealerProfile>(`/operator/dealers/${encodeURIComponent(id)}/profile`);

export const getDealerTimeline = (id: string): Promise<DealerTimelineEvent[]> =>
	request<DealerTimelineEvent[]>(`/operator/dealers/${encodeURIComponent(id)}/timeline`);

export const listDealerLedger = (id: string, query: OperatorListQuery): Promise<Paginated<LedgerEntryRef>> =>
	request<Paginated<LedgerEntryRef>>(`/operator/dealers/${encodeURIComponent(id)}/ledger${qs(query)}`);

export const listDealerTopUps = (id: string, query: OperatorListQuery): Promise<Paginated<DepositTopUp>> =>
	request<Paginated<DepositTopUp>>(`/operator/dealers/${encodeURIComponent(id)}/top-ups${qs(query)}`);

export const listDealerStatusHistory = (id: string, query: OperatorListQuery): Promise<Paginated<DealerStatusChangeRef>> =>
	request<Paginated<DealerStatusChangeRef>>(`/operator/dealers/${encodeURIComponent(id)}/status-history${qs(query)}`);

/** Сторінка Deposits: усі поповнення з дилером і тим, хто вирішив. */
export const listTopUps = (query: OperatorListQuery): Promise<Paginated<OperatorTopUp>> =>
	request<Paginated<OperatorTopUp>>(`/operator/top-ups${qs(query)}`);

export const getTopUpsSummary = (): Promise<OperatorTopUpsSummary> => request<OperatorTopUpsSummary>('/operator/top-ups/summary');

export const getDealersSummary = (): Promise<OperatorDealersSummary> =>
	request<OperatorDealersSummary>('/operator/dealers/summary');

export const listDealers = (query: OperatorListQuery): Promise<Paginated<OperatorDealer>> =>
	request<Paginated<OperatorDealer>>(`/operator/dealers${qs(query)}`);

export const creditBalance = (dealerId: string, command: CreditBalanceCommand): Promise<OperatorDealer> =>
	request<OperatorDealer>(`/operator/dealers/${encodeURIComponent(dealerId)}/ledger`, json(command, 'POST'));


/** Оператори: список, призначення й зняття ролі. Кожна дія лишає слід. */
export const listOperators = (): Promise<OperatorAccount[]> =>
	request<OperatorAccount[]>('/operator/operators');

export const promoteOperator = (command: PromoteOperatorCommand): Promise<OperatorAccount[]> =>
	request<OperatorAccount[]>('/operator/operators', json(command, 'POST'));

export const revokeOperator = (id: string, command: DemoteOperatorCommand): Promise<OperatorAccount[]> =>
	request<OperatorAccount[]>(`/operator/operators/${encodeURIComponent(id)}/revoke`, json(command, 'POST'));

/** Прайс вантажівки перевізника і куди потрапляє кожен майданчик аукціону (рішення 2026-10-22). */
export const getInlandPriceList = (): Promise<InlandPriceList> => request<InlandPriceList>('/operator/inland');

/** Тарифи для адмінки: разом із неактивними портами призначення й неоціненими маршрутами. */
export const getRates = (): Promise<CostRates> => request<CostRates>('/operator/rates');

/** Новий порт призначення: неактивний, з маршрутами без ціни від кожного порту США. */
export const createDestination = (command: CreateDestinationCommand): Promise<CostRates> =>
	request<CostRates>('/operator/destinations', json(command, 'POST'));

/** Що тримає порт: відкриті ставки, авто в дорозі, історія — і що зробить «Remove». */
export const getDestinationUsage = (code: string): Promise<DestinationUsage> =>
	request<DestinationUsage>(`/operator/destinations/${encodeURIComponent(code)}/usage`);

/** Прибрати порт: невикористаний видаляється, з історією — іде в архів. Що заважає — див. usage. */
export const removeDestination = (code: string): Promise<RemoveDestinationResult> =>
	request<RemoveDestinationResult>(`/operator/destinations/${encodeURIComponent(code)}`, { method: 'DELETE' });

/** Повернути порт з архіву: вимкненим, з цінами «Placeholder». */
export const restoreDestination = (code: string): Promise<CostRates> =>
	request<CostRates>(`/operator/destinations/${encodeURIComponent(code)}/restore`, { method: 'POST' });

export const updateRates = (command: UpdateCostRatesCommand): Promise<CostRates> =>
	request<CostRates>('/operator/rates', json(command, 'PATCH'));

export const changeDealerStatus = (id: string, command: ChangeDealerStatusCommand): Promise<OperatorDealer> =>
	request<OperatorDealer>(`/operator/dealers/${encodeURIComponent(id)}/status`, json(command, 'PATCH'));

export const getVehiclesSummary = (): Promise<OperatorVehiclesSummary> =>
	request<OperatorVehiclesSummary>('/operator/vehicles/summary');

export const getInvoicesSummary = (): Promise<OperatorInvoicesSummary> =>
	request<OperatorInvoicesSummary>('/operator/invoices/summary');

export const listVehicles = (query: OperatorListQuery): Promise<Paginated<OperatorVehicle>> =>
	request<Paginated<OperatorVehicle>>(`/operator/vehicles${qs(query)}`);
export const changeVehicleStatus = (id: string, command: ChangeVehicleStatusCommand): Promise<OperatorVehicle> =>
	request<OperatorVehicle>(`/operator/vehicles/${encodeURIComponent(id)}/status`, json(command, 'PATCH'));
export const getPaymentDetails = (): Promise<PaymentDetails | null> => request<PaymentDetails | null>('/payment-details');

export const updatePaymentDetails = (command: UpdatePaymentDetailsCommand): Promise<PaymentDetails> =>
	request<PaymentDetails>('/operator/payment-details', json(command, 'PATCH'));

/** multipart: сам файл, вид документа й необовʼязкова примітка. */
export const uploadVehicleDocument = (
	vehicleId: string,
	input: { file: File; kind: VehicleDocumentKind; note?: string },
): Promise<OperatorVehicle> => {
	const form = new FormData();
	form.append('file', input.file);
	form.append('kind', input.kind);
	if (input.note) form.append('note', input.note);
	// Content-Type не ставимо: межу multipart проставляє браузер сам.
	return request<OperatorVehicle>(`/operator/vehicles/${encodeURIComponent(vehicleId)}/documents`, {
		method: 'POST',
		body: form,
	});
};

export const deleteVehicleDocument = (vehicleId: string, documentId: string): Promise<OperatorVehicle> =>
	request<OperatorVehicle>(
		`/operator/vehicles/${encodeURIComponent(vehicleId)}/documents/${encodeURIComponent(documentId)}`,
		{ method: 'DELETE' },
	);

export const listInvoices = (query: OperatorListQuery): Promise<Paginated<OperatorInvoice>> =>
	request<Paginated<OperatorInvoice>>(`/operator/invoices${qs(query)}`);
export const confirmPayment = (id: string, command: ConfirmPaymentCommand): Promise<Invoice> =>
	request<Invoice>(`/operator/invoices/${encodeURIComponent(id)}/confirm-payment`, json(command, 'POST'));

/** Переказ знайдено у виписці: зарахувати. Без суми — рівно та, що в запиті. */
export const creditTopUp = (id: string, command: CreditTopUpCommand): Promise<DepositTopUp> =>
	request<DepositTopUp>(`/operator/top-ups/${encodeURIComponent(id)}/credit`, json(command, 'POST'));

/** Переказу немає або сума не та. Причину побачить дилер. */
export const declineTopUp = (id: string, command: DeclineTopUpCommand): Promise<DepositTopUp> =>
	request<DepositTopUp>(`/operator/top-ups/${encodeURIComponent(id)}/decline`, json(command, 'POST'));
